import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useManageTrapModalStore } from './ManageTrapStore';

import { AddTrapToObjectRequest } from 'api/traps/types';
import { MLButton } from 'components/Button/Button';
import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import useGetAddMultiTrapsData from 'hooks/Traps/useGetAddMultiTrapsData';
import useAddTrapToObj from 'hooks/Traps/Trap/useAddMultiTrapToObj';
import { TrapTableType } from 'components/TrapsTables/trapTables';
import MLTextArea from 'components/Input/TextArea';

const convertTrapType = (type: string): TrapTableType | undefined => {
  switch (type) {
    case 'mct':
      return TrapTableType.MultiCatchMouseTraps;
    case 'srbm':
      return TrapTableType.SmartRodenBaitModule;
    case 'bs':
      return TrapTableType.BaitStations;
    case 'cit':
      return TrapTableType.CrawlingInsectsTrap;
    case 'fit':
      return TrapTableType.FeromonTraps;
    case 'smst':
      return TrapTableType.SnapTrapMouse;
    case 'srst':
      return TrapTableType.SnapTrapRat;
    default:
      return undefined;
  }
};

interface ManageTrapModalProps {
  refetchTraps: () => void;
}

const ManageTrapModal: React.FC<ManageTrapModalProps> = ({ refetchTraps }) => {
  const { isOpen, onManageTrapModalClose: onMultiTrapModalClose, slug, trapType } = useManageTrapModalStore();
  const { trapOptions } = useGetAddMultiTrapsData(slug);

  const { addTrapToObject } = useAddTrapToObj(trapType || TrapTableType.MultiCatchMouseTraps);

  const { t } = useTranslation();

  const traps = useMemo(() => {
    return trapOptions?.traps.filter(trap => convertTrapType(trap.trap_type) === trapType);
  }, [trapOptions, trapType]);

  const { handleSubmit, control, reset } = useForm<AddTrapToObjectRequest>({
    defaultValues: {
      trap: '',
      sequence_number: undefined,
      date_of_installation: new Date().toISOString().split('T')[0],
      installment_comment: '',
      recomended_bait_filling: '',
      hub: undefined,
    },
  });

  const onReset = () => {
    reset();
    onMultiTrapModalClose();
  };

  const addTrapsToObject = (data: AddTrapToObjectRequest) => {
    addTrapToObject(
      { addData: data, slug },
      {
        onSuccess: () => {
          enqueueSnackbar(t('TrapModals.manageModal.successfullyAdded'), {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchTraps();
        },
        onError: () => {
          enqueueSnackbar(t('TrapModals.manageModal.errorAdding'), {
            variant: 'error',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={onReset} size="md">
        <ModalHeader toggle={onReset} className="border-bottom pb-2 pt-3 pb-3 ">
          <div className="text-purple">
            {trapType === TrapTableType.BaitStations
              ? t('TrapModals.manageModal.addBaitStation')
              : t('TrapModals.manageModal.addTrap', {
                  trapType: trapType,
                })}
          </div>
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="row">
              <div className="mb-3 col-md-8">
                <Controller
                  control={control}
                  name="trap"
                  rules={{ required: 'Required field' }}
                  render={({ field, fieldState: { error } }) => (
                    <MLSelect
                      {...field}
                      label={
                        trapType === TrapTableType.BaitStations
                          ? t('TrapModals.manageModal.baitStation')
                          : t('TrapModals.manageModal.trap')
                      }
                      error={error?.message}
                      value={field.value}
                      required
                      onChange={selectedOption => {
                        field.onChange(selectedOption);
                      }}
                      isDisabled={!traps || traps.length === 0}
                      options={
                        !traps || traps.length === 0
                          ? [{ value: '', label: 'No traps' }]
                          : [
                              {
                                value: '',
                                label:
                                  trapType === TrapTableType.BaitStations
                                    ? t('TrapModals.manageModal.selectStation')
                                    : t('TrapModals.manageModal.selectTrap'),
                              },
                              ...traps.map(trap => ({
                                value: trap.pk.toString(),
                                label: trap.serial_number,
                              })),
                            ]
                      }
                    />
                  )}
                />
              </div>
              <div className="mb-3 col-md-4">
                <Controller
                  control={control}
                  name="sequence_number"
                  rules={{ required: 'Required field', min: { value: 1, message: 'Sequence number must be positive' } }}
                  render={({ field, formState: { errors } }) => (
                    <MLInput
                      {...field}
                      label={t('TrapModals.manageModal.sequenceNumber')}
                      error={errors?.sequence_number?.message}
                      required
                      type="number"
                      onChange={e => field.onChange(+e.target.value)}
                    />
                  )}
                />
              </div>
            </div>
            {trapType === TrapTableType.MultiCatchMouseTraps ||
            trapType === TrapTableType.SnapTrapMouse ||
            trapType === TrapTableType.SnapTrapRat ||
            trapType === TrapTableType.SmartRodenBaitModule ? (
              <>
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <Controller
                      name="hub"
                      control={control}
                      rules={{ required: 'Required field' }}
                      render={({ field, formState: { errors } }) => (
                        <MLSelect
                          {...field}
                          label={t('TrapModals.manageModal.hub')}
                          required
                          error={errors?.hub?.message}
                          onChange={selectedOption => {
                            field.onChange(selectedOption);
                          }}
                          {...(!trapOptions?.hubs || trapOptions?.hubs.length === 0
                            ? {
                                disabled: true,
                                options: [{ value: '', label: 'No Hubs' }],
                              }
                            : {
                                options: [{ value: '', label: `${t('TrapModals.manageModal.selectHub')}` }].concat(
                                  trapOptions?.hubs.map(hub => ({
                                    value: hub.pk.toString(),
                                    label: hub.serial_number,
                                  })),
                                ),
                              })}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="mb-3 col-md-12">
                    <Controller
                      name="installment_comment"
                      control={control}
                      render={({ field, formState: { errors } }) => (
                        <MLTextArea
                          {...field}
                          inputClassName="form-control"
                          label={t('TrapModals.manageModal.installmentComment')}
                          tooltip={t('TrapModals.manageModal.installmentCommentTooltip')}
                          error={errors?.installment_comment?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </>
            ) : null}

            {trapType === TrapTableType.SmartRodenBaitModule || trapType === TrapTableType.BaitStations ? (
              <div className="row">
                <div className="mb-3 col-md-12">
                  <Controller
                    name="recomended_bait_filling"
                    control={control}
                    rules={{
                      required: 'Required field',
                      min: { value: 1, message: 'Recommended bait filling must be positive' },
                    }}
                    render={({ field, formState: { errors } }) => (
                      <MLInput
                        {...field}
                        required
                        label={t('TrapModals.manageModal.recommendedBaitFilling')}
                        error={errors?.recomended_bait_filling?.message}
                        type="number"
                      />
                    )}
                  />
                </div>
              </div>
            ) : null}

            <div className="row">
              <div className="mb-3 col-md-6">
                <Controller
                  name="installment_picture"
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <MLInput
                      label={t('TrapModals.manageModal.installmentPicture')}
                      type="file"
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        field.onChange(file);
                      }}
                      tooltip={t('TrapModals.manageModal.installmentPictureTooltip', {
                        trapType:
                          trapType === TrapTableType.SmartRodenBaitModule
                            ? t('TrapModals.manageModal.baitStation')
                            : t('TrapModals.manageModal.trap'),
                      })}
                      error={errors?.installment_picture?.message}
                    />
                  )}
                />
              </div>
              <div className="mb-3 col-md-6">
                <Controller
                  name="date_of_installation"
                  rules={{ required: 'Required field' }}
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <MLInput
                      label={t('TrapModals.manageModal.dateOfInstallation')}
                      type="date"
                      required
                      tooltip={t('TrapModals.manageModal.dateOfInstallationTooltip', {
                        trapType:
                          trapType === TrapTableType.SmartRodenBaitModule
                            ? t('TrapModals.manageModal.baitStation')
                            : t('TrapModals.manageModal.trap'),
                      })}
                      {...field}
                      error={errors?.date_of_installation?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="w-100 d-flex justify-content-end">
            <MLButton
              onClick={handleSubmit(addTrapsToObject)}
              isGhost
              color="success"
              innerText={t('TrapModals.confirm')}
            />
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageTrapModal;
